import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useRef, useState } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { getLocalFlag, setLocalFlag } from "@/lib/local-flags";
import { logRocketRedactProps } from "@/lib/logrocket";
import { normalizeOperatorEmail, STAFF_OPERATOR_EMAIL_KEY } from "@/lib/staff-login";
import { colors, fonts, gradients, radius, shadows, spacing } from "@/lib/theme";

function PinwheelLogo({ size = 34 }: { size?: number }) {
    const half = size / 2;
    return (
        <View style={{ width: size, height: size }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </View>
    );
}

export default function PhoneScreen() {
    const router = useRouter();
    const { requestOtp } = useAuth();

    const inputRef = useRef<TextInput>(null);
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [focused, setFocused] = useState(false);

    // Hidden staff login: tapping the logo 5× reveals a password field. With a
    // correct password the backend skips the SMS and accepts the fixed code
    // 456789, so an admin/tester can log in as any nurse without texting her.
    const logoTaps = useRef(0);
    const [adminMode, setAdminMode] = useState(false);
    const [adminPassword, setAdminPassword] = useState("");
    // Her own staff email, sent with the password so the server records who
    // used it (security plan B2, lib/staff-login.ts). Remembered on this
    // device after a staff sign-in that worked; the password never is.
    const [operatorEmail, setOperatorEmail] = useState("");

    function handleLogoTap() {
        logoTaps.current += 1;
        if (logoTaps.current >= 5) {
            logoTaps.current = 0;
            setAdminMode(true);
            void getLocalFlag(STAFF_OPERATOR_EMAIL_KEY).then((saved) => {
                if (saved) {
                    setOperatorEmail((typed) => typed || saved);
                }
            });
        }
    }

    // autoFocus dismisses the keyboard on real devices because the navigation
    // transition steals focus before the screen is fully settled. Focus manually
    // after the screen is visible instead.
    useFocusEffect(
        useCallback(() => {
            const t = setTimeout(() => inputRef.current?.focus(), 300);
            return () => clearTimeout(t);
        }, [])
    );

    function formatPhone(raw: string): string {
        const digits = raw.replace(/[^\d]/g, "");
        if (digits.length <= 3) {
            return digits;
        }
        if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        }
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }

    function handlePhoneChange(text: string) {
        const digits = text.replace(/[^\d]/g, "");
        if (digits.length <= 10) {
            setPhone(digits);
        }
        setError(null);
    }

    async function handleSubmit() {
        const digits = phone.replace(/[^\d]/g, "");
        if (digits.length < 10) {
            setError("Please enter a valid 10-digit phone number");
            return;
        }

        setLoading(true);
        setError(null);

        const fullPhone = `+1${digits}`;
        const staffPassword = adminMode ? adminPassword.trim() : "";
        const staffEmail = staffPassword ? normalizeOperatorEmail(operatorEmail) : "";
        const result = await requestOtp(
            fullPhone,
            staffPassword || undefined,
            staffEmail || undefined
        );

        setLoading(false);
        if (result.success) {
            if (staffEmail) {
                // The server accepted it as an active staff account.
                void setLocalFlag(STAFF_OPERATOR_EMAIL_KEY, staffEmail);
            }
            router.push("/(auth)/verify");
        } else {
            setError(result.error ?? "Failed to send code");
        }
    }

    const isValid = phone.replace(/[^\d]/g, "").length === 10;

    return (
        <SafeAreaView style={styles.container}>
            <KeyboardAvoidingView
                style={styles.keyboardView}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                {/* Back button */}
                <TouchableOpacity
                    onPress={() => router.replace("/(auth)/welcome")}
                    hitSlop={12}
                    style={styles.backButton}
                >
                    <MaterialCommunityIcons
                        name="arrow-left"
                        size={22}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>

                <View style={styles.content}>
                    {/* Logo — also the hidden staff-login trigger (tap 5×) */}
                    <TouchableOpacity
                        activeOpacity={1}
                        onPress={handleLogoTap}
                        style={styles.logoArea}
                    >
                        <PinwheelLogo size={52} />
                    </TouchableOpacity>

                    {/* Title */}
                    <Text style={styles.title}>Enter your phone number</Text>
                    <Text style={styles.subtitle}>We'll send you a verification code</Text>

                    {/* Phone Input */}
                    <View
                        style={[
                            styles.inputCard,
                            focused && styles.inputCardFocused,
                            error ? styles.inputCardError : null
                        ]}
                    >
                        <Text style={styles.inputPrefix}>+1</Text>
                        <TextInput
                            ref={inputRef}
                            style={styles.input}
                            value={formatPhone(phone)}
                            onChangeText={handlePhoneChange}
                            onFocus={() => setFocused(true)}
                            onBlur={() => setFocused(false)}
                            keyboardType="phone-pad"
                            placeholder="(555) 555-5555"
                            placeholderTextColor={colors.textTertiary}
                            maxLength={14}
                        />
                    </View>

                    {/* Hidden staff password field (revealed by tapping the logo 5×).
                        Redacted from LogRocket replays: the password signs in as
                        any nurse, so it must never be readable in a recording. */}
                    {adminMode && (
                        <View
                            {...logRocketRedactProps()}
                            style={[styles.inputCard, styles.adminInputCard]}
                        >
                            <MaterialCommunityIcons
                                name="shield-key-outline"
                                size={18}
                                color={colors.textTertiary}
                            />
                            <TextInput
                                style={styles.input}
                                value={adminPassword}
                                onChangeText={setAdminPassword}
                                placeholder="Staff password"
                                placeholderTextColor={colors.textTertiary}
                                secureTextEntry
                                autoCapitalize="none"
                                autoCorrect={false}
                            />
                        </View>
                    )}
                    {/* Who is signing in: the operator's own staff email. */}
                    {adminMode && (
                        <View
                            {...logRocketRedactProps()}
                            style={[styles.inputCard, styles.adminInputCard]}
                        >
                            <MaterialCommunityIcons
                                name="account-outline"
                                size={18}
                                color={colors.textTertiary}
                            />
                            <TextInput
                                style={[styles.input, styles.operatorInput]}
                                value={operatorEmail}
                                onChangeText={setOperatorEmail}
                                placeholder="Your staff email"
                                placeholderTextColor={colors.textTertiary}
                                keyboardType="email-address"
                                autoComplete="email"
                                textContentType="emailAddress"
                                autoCapitalize="none"
                                autoCorrect={false}
                            />
                        </View>
                    )}

                    {error && (
                        <View style={styles.errorRow}>
                            <MaterialCommunityIcons
                                name="alert-circle"
                                size={14}
                                color={colors.error500}
                            />
                            <Text style={styles.errorText}>{error}</Text>
                        </View>
                    )}
                </View>

                {/* Submit */}
                <View style={styles.bottomArea}>
                    <TouchableOpacity
                        activeOpacity={isValid ? 0.8 : 1}
                        onPress={handleSubmit}
                        disabled={loading || !isValid}
                        style={styles.submitButtonWrapper}
                    >
                        {isValid ? (
                            <LinearGradient
                                colors={[...gradients.brand]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={styles.submitButton}
                            >
                                <Text style={styles.submitText}>
                                    {loading ? "Sending..." : "Continue"}
                                </Text>
                            </LinearGradient>
                        ) : (
                            <View style={styles.submitButtonDisabled}>
                                <Text style={styles.submitTextDisabled}>Continue</Text>
                            </View>
                        )}
                    </TouchableOpacity>

                    <Text style={styles.termsText}>
                        By continuing, you agree to our Terms of Service and Privacy Policy
                    </Text>
                </View>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    keyboardView: {
        flex: 1
    },
    backButton: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.md,
        alignSelf: "flex-start"
    },
    content: {
        flex: 1,
        paddingHorizontal: spacing.xl,
        justifyContent: "center",
        alignItems: "center"
    },
    logoArea: {
        marginBottom: spacing["2xl"]
    },
    title: {
        fontSize: 22,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        textAlign: "center"
    },
    subtitle: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginTop: spacing.sm,
        marginBottom: spacing["2xl"],
        textAlign: "center"
    },
    inputCard: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 2,
        borderColor: colors.border,
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.md,
        gap: spacing.sm,
        width: "100%"
    },
    inputCardFocused: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    inputCardError: {
        borderColor: colors.error400
    },
    adminInputCard: {
        marginTop: spacing.md
    },
    // An email is longer than a phone: smaller, without the digit spacing.
    operatorInput: {
        fontSize: 15,
        letterSpacing: 0
    },
    inputPrefix: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textTertiary
    },
    input: {
        flex: 1,
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        padding: 0,
        textAlign: "center",
        letterSpacing: 1
    },
    errorRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginTop: spacing.md
    },
    errorText: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.error500
    },
    bottomArea: {
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing["2xl"],
        alignItems: "center"
    },
    submitButtonWrapper: {
        width: "100%"
    },
    submitButton: {
        paddingVertical: 16,
        borderRadius: 20,
        alignItems: "center",
        ...shadows.brand
    },
    submitButtonDisabled: {
        paddingVertical: 16,
        borderRadius: 20,
        alignItems: "center",
        backgroundColor: colors.gray200
    },
    submitText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    submitTextDisabled: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.gray400
    },
    termsText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        textAlign: "center",
        marginTop: spacing.base,
        paddingHorizontal: spacing.lg
    }
});
